import { FormattedMessage } from '@flipster/i18n';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import TradeUpIcon from '@flipster/ui/assets/svgs/unfilled/trade-up.svg';
import segment from '@flipster/utils/segment';
import { MarketEvent } from '@flipster/utils/segment/events/market';

import { Sort, TabValueType } from '../types';
import { getMarketTrendSort, marketTrendDiscoverTab } from '../utils';

interface Props {
  isLoading: boolean;
  trend: 'up' | 'down';
  isMoreThanOrEqualOpposite: boolean;
  coinCount: number;
  oppositeCoinCount: number;
  onChangeTab: (tab: TabValueType, sort?: Sort) => void;
}

export const TrendInfo = ({
  isLoading,
  trend,
  isMoreThanOrEqualOpposite,
  coinCount,
  oppositeCoinCount,
  onChangeTab,
}: Props) => {
  const { isMobile } = useResponsive();

  const onClickTrendInfo = () => {
    onChangeTab(marketTrendDiscoverTab, getMarketTrendSort(trend));
    const down = trend === 'down' ? coinCount : oppositeCoinCount;
    const up = trend === 'up' ? coinCount : oppositeCoinCount;
    segment.track(MarketEvent.MarketTrendClick, {
      clicked_market_trend: trend,
      down_coin_count: down,
      up_coin_count: up,
    });
  };

  const coinExists = coinCount > 0;
  const more = isMoreThanOrEqualOpposite && coinExists;
  if (trend === 'up') {
    return (
      <button
        type='button'
        className={hstack({
          gap: isLoading ? 2 : 4,
          color: isLoading ? 'content.default.level-1' : more ? 'content.state.positive' : 'content.default.level-2',
        })}
        onClick={onClickTrendInfo}
      >
        <TradeUpIcon className={iconCss} />
        <span className={textCss}>
          {isLoading ? (
            <div className={loadingCountCss} />
          ) : (
            <FormattedMessage
              id={isMobile ? 'Market.r250925.Amount_up' : 'Market.r250925.Amount_symbols_up'}
              values={{ amount: coinCount }}
            />
          )}
        </span>
      </button>
    );
  }

  return (
    <button
      type='button'
      className={hstack({
        gap: isLoading ? 2 : 4,
        color: isLoading ? 'content.default.level-1' : more ? 'content.state.negative' : 'content.default.level-2',
      })}
      onClick={onClickTrendInfo}
    >
      <span className={textCss}>
        {isLoading ? (
          <div className={loadingCountCss} />
        ) : (
          <FormattedMessage
            id={isMobile ? 'Market.r250925.Amount_down' : 'Market.r250925.Amount_symbols_down'}
            values={{ amount: coinCount }}
          />
        )}
      </span>
      <TradeUpIcon className={cx(iconCss, css({ transform: 'rotate(90deg)' }))} />
    </button>
  );
};

const textCss = css({ textStyle: 'Text/M/Regular', display: 'flex', gap: 4 });
const iconCss = css({
  tabletDown: { width: 20, height: 20 },
  tablet: { width: 24, height: 24 },
});
const loadingCountCss = css({
  width: { base: 20, tablet: 24 },
  height: 16,
  backgroundColor: 'background.default.level-3',
  borderRadius: 4,
});
